import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MotifButtonModule,
  MotifIconModule,
  MotifModalModule,
  MotifFormsModule,
  MotifDatePickerModule,
  MotifModalRef,
  MOTIF_MODAL_DATA,
} from '@ey-xd/ng-motif';
import { WorkspaceService } from '@sa/shared/services/workspace.service';
import { CommonService } from '@sa/shared/services/common.service';
import { LoggerService } from '@sa/core/services/logger.service';
import { TransitionReplicationRequest } from '@sa/shared/interfaces/DeliverableReplication';
import {
  JurisdictionResponse,
  FiscalYearResponse,
  PeriodResponse,
  Country,
  LegalEntity,
} from '@sa/shared/interfaces/JurisdictionEntityFiscalResponse';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-transition-replicate-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MotifButtonModule,
    MotifIconModule,
    MotifModalModule,
    MotifFormsModule,
    MotifDatePickerModule,
  ],
  templateUrl: './transition-replicate-panel.component.html',
  styleUrls: ['./transition-replicate-panel.component.scss'],
})
export class TransitionReplicatePanelComponent implements OnInit, OnDestroy {
  entities: LegalEntity[] = [];
  filteredEntities: LegalEntity[] = [];
  countries: Country[] = [];
  years: FiscalYearResponse[] = [];
  periods: PeriodResponse[] = [];
  frequencies: string[] = ['Monthly', 'Quarterly', 'Annual'];

  selectedEntity: LegalEntity | null = null;
  selectedCountry: Country | null = null;
  selectedYear: FiscalYearResponse | null = null;
  selectedPeriod: PeriodResponse | null = null;
  selectedFrequency: string | null = null;
  filingStartDate: string = '';
  filingEndDate: string = '';
  statutoryDueDate: string = '';
  mappingTemplateId: string = '';
  obligationId: string = '';
  isSubmitting: boolean = false;

  // Track interaction on the date fields so an empty required date only shows
  // the red error state after the user has touched/cleared it (not on load).
  startDateTouched = false;
  endDateTouched = false;
  statDueDateTouched = false;

  private countryEntityMap = new Map<string, LegalEntity[]>();

  private readonly _workspaceService = inject(WorkspaceService);
  private readonly _commonService = inject(CommonService);
  private readonly _loggerService = inject(LoggerService);
  private readonly _modalRef = inject(MotifModalRef);
  private readonly _modalData = inject(MOTIF_MODAL_DATA);
  private subscriptions: Subscription[] = [];

  get isReplicateDisabled(): boolean {
    return (
      this.isSubmitting ||
      !this.selectedEntity ||
      !this.selectedCountry ||
      !this.selectedYear ||
      !this.selectedPeriod ||
      !this.selectedFrequency ||
      !this.filingStartDate ||
      !this.filingEndDate ||
      !this.statutoryDueDate ||
      !this.mappingTemplateId ||
      !this.obligationId
    );
  }

  onStartDateChange(event: { value: Date | null }): void {
    this.startDateTouched = true;
    if (event.value) {
      const year = event.value.getFullYear();
      const month = String(event.value.getMonth() + 1).padStart(2, '0');
      const day = String(event.value.getDate()).padStart(2, '0');
      this.filingStartDate = `${year}-${month}-${day}`;
    } else {
      this.filingStartDate = '';
    }
  }

  onEndDateChange(event: { value: Date | null }): void {
    this.endDateTouched = true;
    if (event.value) {
      const year = event.value.getFullYear();
      const month = String(event.value.getMonth() + 1).padStart(2, '0');
      const day = String(event.value.getDate()).padStart(2, '0');
      this.filingEndDate = `${year}-${month}-${day}`;
    } else {
      this.filingEndDate = '';
    }
  }

  onStatDueDateChange(event: { value: Date | null }): void {
    this.statDueDateTouched = true;
    if (event.value) {
      const year = event.value.getFullYear();
      const month = String(event.value.getMonth() + 1).padStart(2, '0');
      const day = String(event.value.getDate()).padStart(2, '0');
      this.statutoryDueDate = `${year}-${month}-${day}`;
    } else {
      this.statutoryDueDate = '';
    }
  }

  onClearStartDate(): void {
    this.filingStartDate = '';
    this.startDateTouched = true;
  }

  onClearEndDate(): void {
    this.filingEndDate = '';
    this.endDateTouched = true;
  }

  onClearStatDueDate(): void {
    this.statutoryDueDate = '';
    this.statDueDateTouched = true;
  }

  periodSortComparator(a: any, b: any): number {
    return a.periodId - b.periodId;
  }

  ngOnInit(): void {
    this.loadJurisdictionData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadJurisdictionData(): void {
    const tenantGuid = this._commonService.getState('selectedTenantGuid') || '';
    if (!tenantGuid) {
      this._loggerService.logError('Tenant information is missing');
      return;
    }

    const sub = this._workspaceService.getJurisdictionEntityFiscal(tenantGuid).subscribe({
      next: (response) => {
        this.countries = response.jurisdictionResponses.map(j => j.country);
        const allEntities = response.jurisdictionResponses.flatMap(j => j.legalEntities);
        const seen = new Set<string>();
        this.entities = allEntities.filter(e => {
          if (seen.has(e.entityGuid)) return false;
          seen.add(e.entityGuid);
          return true;
        });

        // Populate years and periods from the same response the dashboard uses.
        // Exclude the 'annual' period so the list matches the dashboard dropdown.
        this.years = response.fiscalYearResponses ?? [];
        this.periods = (response.periodResponses ?? []).filter(
          period => (period.periodName || '').trim().toLowerCase() !== 'annual'
        );

        // Build country-to-entities mapping so the Entity dropdown can be
        // filtered to only the entities of the selected country.
        this.countryEntityMap.clear();
        response.jurisdictionResponses.forEach(j => {
          const existing = this.countryEntityMap.get(j.country.countryGuid) || [];
          this.countryEntityMap.set(j.country.countryGuid, [
            ...existing,
            ...j.legalEntities,
          ]);
        });
      },
      error: (err: Error) => {
        this._loggerService.logApiError('Failed to load jurisdiction data');
        console.error(err);
      },
    });
    this.subscriptions.push(sub);
  }

  onSelectCountry(event: Country): void {
    this.selectedCountry = event;
    // Filter the Entity dropdown to only the entities of the selected country.
    const countryEntities = event
      ? this.countryEntityMap.get(event.countryGuid) || []
      : [];
    const seen = new Set<string>();
    this.filteredEntities = countryEntities.filter(e => {
      if (seen.has(e.entityGuid)) return false;
      seen.add(e.entityGuid);
      return true;
    });
    // Reset the previously selected entity since it may not belong to the new country.
    this.selectedEntity = null;
  }

  onSelectEntity(event: any): void {
    this.selectedEntity = event;
  }

  onReplicate(): void {
    if (this.isReplicateDisabled || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    const tenantGuid = this._commonService.getState('selectedTenantGuid') || '';
    const userGuid = this._commonService.getState('userGuid') || null;

    if (!tenantGuid) {
      this._loggerService.logError('Tenant information is missing');
      return;
    }

    const request: TransitionReplicationRequest = {
      entityGuid: this.selectedEntity!.entityGuid,
      country: this.selectedCountry!.countryName,
      complianceYear: parseInt(this.selectedYear!.auditYear, 10),
      compliancePeriod: this.selectedPeriod!.periodName,
      frequency: this.selectedFrequency!,
      filingStartDate: this.filingStartDate,
      filingEndDate: this.filingEndDate,
      statutoryDueDate: this.statutoryDueDate,
      mappingTemplateId: this.mappingTemplateId,
      obligationId: this.obligationId,
      tenantGuid: tenantGuid,
      userGuid: userGuid,
    };

    const sub = this._workspaceService.replicateTransitionDeliverables(tenantGuid, request).subscribe({
      next: (response) => {
        if (response.success) {
          this._loggerService.logSuccess('Transition replication completed successfully');
          this._modalRef.close(true);
        } else {
          this._loggerService.logError(response.message || 'Transition replication failed');
          this.isSubmitting = false;
        }
      },
      error: (err: Error) => {
        this._loggerService.logApiError('Transition replication failed');
        console.error(err);
        this.isSubmitting = false;
      },
    });
    this.subscriptions.push(sub);
  }
}
